import React, { Fragment } from 'react';

import { LegacyButton } from '@zapier/design-system';

import { BASE_DOC_URL_VB, NEW_SERVICE_FIELD } from 'app/developer-v3/constants';
import {
  useAvroEvent,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';
import { getAppVersionUrl, getTriggerUrl } from 'app/developer-v3/utils';

import * as CTAs from './CTAs';
import { getLockedHints, getReadOnlyReasons } from './utils';

import type { ZapTaskDetails } from '../types';

type Props = {
  appId: ID;
  hasAuthentication: boolean;
  hasTriggers: boolean;
  ordinal: number;
  userCount: number;
  version: string;
  zapTaskDetails: ZapTaskDetails['triggers'];
};

function CTATriggers({
  appId,
  hasAuthentication,
  hasTriggers,
  ordinal,
  userCount,
  version,
  zapTaskDetails,
}: Props) {
  const { emitPartnerUserInteractionEvent } = useAvroEvent();
  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const {
    isReadOnly,
    adminOnlyHint,
    lifecyclePendingHint,
    productionOrUserCountLimitExceededHint,
  } = getReadOnlyReasons(currentVersionReadOnlyReason, userCount);
  const lockedHints = getLockedHints(userCount);
  const hint = isReadOnly
    ? adminOnlyHint ||
      lifecyclePendingHint ||
      productionOrUserCountLimitExceededHint
    : lockedHints.needAuth;

  const appVersionUrl = getAppVersionUrl(appId, version);
  const description =
    'Help users find new data as soon as it’s available in your app. Triggers connect to webhooks or polling endpoints.';
  const learnMoreUrl = `${BASE_DOC_URL_VB}/build/trigger`;
  const actionUrl = getTriggerUrl({
    id: appId,
    triggerKey: NEW_SERVICE_FIELD,
    version,
  });

  return !isReadOnly && hasAuthentication && hasTriggers ? (
    <CTAs.Dropdown
      action={
        <Fragment>
          <LegacyButton
            aria-label="Add Triggers"
            href={actionUrl}
            onClick={() => {
              emitPartnerUserInteractionEvent({
                eventAction: 'click',
                interactionGoal: 'Add a trigger',
                interactionName: 'integration_home_button_clicked',
                interactionRawText: 'Add Trigger',
                optionSelected: 'trigger',
              });
            }}
          >
            Add Trigger
          </LegacyButton>
          <LegacyButton
            aria-label="Manage Triggers"
            href={`${appVersionUrl}/triggers`}
            onClick={() => {
              emitPartnerUserInteractionEvent({
                eventAction: 'click',
                interactionGoal: 'Manage triggers',
                interactionName: 'integration_home_button_clicked',
                interactionRawText: 'Manage Triggers',
                optionSelected: 'trigger',
              });
            }}
          >
            Manage Triggers
          </LegacyButton>
        </Fragment>
      }
      description={description}
      icon="trigger"
      isActive={false}
      ordinal={ordinal}
      title="Triggers"
      type="triggers"
      zapTaskDetails={zapTaskDetails}
    />
  ) : (
    <CTAs.Lockable
      action={
        <LegacyButton
          aria-label="Create a Trigger"
          href={actionUrl}
          onClick={() => {
            emitPartnerUserInteractionEvent({
              eventAction: 'click',
              interactionGoal: 'Start creating a trigger',
              interactionName: 'integration_home_button_clicked',
              interactionRawText: 'Create a Trigger',
              optionSelected: 'trigger',
            });
          }}
        >
          Create a Trigger
        </LegacyButton>
      }
      description={description}
      hint={hint}
      icon="trigger"
      isActive={false}
      isLocked={isReadOnly || !hasAuthentication}
      learnMoreUrl={learnMoreUrl}
      ordinal={ordinal}
      title="Triggers"
    />
  );
}

export default CTATriggers;
